import { Link } from 'react-router-dom';
import { ArrowUpRight, ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ButtonColorful } from '@/components/ui/button-colorful';
import { LandingMarkdown } from './LandingMarkdown';
import { ALIGN_ITEMS, ALIGN_JUSTIFY, ALIGN_TEXT, CARD_HOVER, DISPLAY_TITLE, EYEBROW } from './landing-styles';
import { applyLandingVars, useLandingVars, type LandingVars } from './landing-vars';
import { AuroraBackground, GridBackground, SpotlightBackground, useHeroPointer } from './hero-backgrounds';
import { NewsBlock, ProgressBlock, TeamBlock, VideoBlock } from './DynamicBlocks';
import { useLightbox } from './useLightbox';
import type { LandingBlock } from '../../../../shared/types/people-landing';



/** Espejo del allowlist del backend (people-landing-schema.ts). */
export function isAllowedEmbedUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    const hosts = [
      'www.youtube.com',
      'youtube.com',
      'www.youtube-nocookie.com',
      'youtube-nocookie.com',
      'drive.google.com',
      'docs.google.com',
    ];
    if (!hosts.includes(u.hostname)) return false;
    if (u.hostname.includes('youtube')) return u.pathname.startsWith('/embed/');
    return /\/(preview|embed)(\/|$)/.test(u.pathname);
  } catch {
    return false;
  }
}

function SmartLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

function MissingImage({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-xl bg-muted/40 text-muted-foreground text-sm h-40">
      <ImageOff className="h-4 w-4" />
      {label ?? 'Imagen'}
    </div>
  );
}

interface BlockRendererProps {
  block: LandingBlock;
  imageUrls: Record<string, string>;
}

export function BlockRenderer({ block, imageUrls }: BlockRendererProps) {
  const vars = useLandingVars();
  switch (block.type) {
    case 'hero':
      return <HeroBlock block={block} imageUrls={imageUrls} vars={vars} />;

    case 'richtext':
      return (
        <div className={cn('max-w-none', ALIGN_TEXT[block.align ?? 'left'])}>
          {block.title && (
            <h2 className={cn(DISPLAY_TITLE, 'text-3xl md:text-4xl mb-4')}>{applyLandingVars(block.title, vars)}</h2>
          )}
          <LandingMarkdown content={applyLandingVars(block.markdown, vars)} size="lg" />
        </div>
      );

    case 'image': {
      const url = imageUrls[block.imageKey];
      // Tamaños tipo site: la mayoría de las imágenes del Google Site eran
      // chicas/medianas — 'md' es el default; 'full' es el ancho completo.
      const sizeClass = {
        sm: 'max-h-36 max-w-xs',
        md: 'max-h-64 max-w-xl',
        lg: 'max-h-96 max-w-3xl',
        full: 'max-h-[600px] max-w-full',
      }[block.size ?? 'md'];
      const img = url ? (
        <img
          src={url}
          alt={block.caption ?? ''}
          className={cn('rounded-xl border border-border/60 w-auto object-contain', sizeClass)}
        />
      ) : (
        <MissingImage label={block.caption} />
      );
      return (
        <figure className="flex flex-col items-center gap-2">
          {block.url ? (
            <SmartLink href={block.url} className="hover:opacity-90 transition-opacity">
              {img}
            </SmartLink>
          ) : url ? (
            <ZoomableImage url={url}>{img}</ZoomableImage>
          ) : (
            img
          )}
          {block.caption && (
            <figcaption className="text-sm text-muted-foreground">{block.caption}</figcaption>
          )}
        </figure>
      );
    }

    case 'cards':
      return <CardsBlock block={block} imageUrls={imageUrls} />;

    case 'links':
      return (
        <div>
          {block.title && <h2 className={cn(DISPLAY_TITLE, 'text-3xl md:text-4xl mb-4')}>{block.title}</h2>}
          <div className="flex flex-wrap gap-3">
            {block.items.map((item) => (
              <ButtonColorful key={item.id} href={item.url} label={item.title} />
            ))}
          </div>
          {block.items.some((i) => i.description) && (
            <div className="mt-2 space-y-1">
              {block.items
                .filter((i) => i.description)
                .map((i) => (
                  <p key={i.id} className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{i.title}:</span> {i.description}
                  </p>
                ))}
            </div>
          )}
        </div>
      );

    case 'embed': {
      if (!isAllowedEmbedUrl(block.url)) {
        return (
          <a
            href={block.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary underline underline-offset-2 text-sm"
          >
            <ArrowUpRight className="h-4 w-4" />
            {block.title || block.url}
          </a>
        );
      }
      return (
        <figure className="space-y-2">
          {block.title && (
            <figcaption className="text-sm font-medium text-muted-foreground">
              {block.title}
            </figcaption>
          )}
          {/* Sin referrerPolicy: YouTube necesita el referrer para validar el
              dominio que embebe — con no-referrer tira error 153. */}
          <div className="aspect-video w-full max-w-4xl overflow-hidden rounded-2xl border border-border/60 bg-black">
            <iframe
              src={block.url}
              title={block.title ?? 'Embed'}
              className="h-full w-full"
              sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms"
              allowFullScreen
              allow="autoplay; encrypted-media; picture-in-picture"
            />
          </div>
        </figure>
      );
    }

    case 'divider':
      return <hr className="border-border/60" />;

    case 'callout': {
      // Un acento dominante por bloque: fondo suave de la paleta (pBg/gBg/bBg/oBg).
      const tone = {
        purple: 'bg-[#F3EEFF] border-[#D5C4FF] dark:bg-[#2a2440] dark:border-[#4a3d6e]',
        green: 'bg-[#E5F9F7] border-[#B6EDE7] dark:bg-[#12332f] dark:border-[#1f5f58]',
        blue: 'bg-[#EBF7FF] border-[#BFE6FF] dark:bg-[#132a38] dark:border-[#1f4a63]',
        orange: 'bg-[#FEF1EC] border-[#FFD1C0] dark:bg-[#3a2219] dark:border-[#6b3d2c]',
      }[block.tone ?? 'purple'];
      const body = (
        <div
          className={cn(
            'relative h-full min-h-[160px] rounded-2xl border p-6 flex flex-col gap-2 transition-all duration-300',
            tone,
            block.url && 'hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(28,201,185,0.12)]'
          )}
        >
          {block.eyebrow && <span className={EYEBROW}>{applyLandingVars(block.eyebrow, vars)}</span>}
          <h3 className={cn(DISPLAY_TITLE, 'text-2xl md:text-3xl leading-[1.15] text-brand-dark dark:text-white')}>
            {applyLandingVars(block.title, vars)}
          </h3>
          {block.text && (
            <p className="text-sm text-brand-dark/70 dark:text-white/70 text-pretty">{applyLandingVars(block.text, vars)}</p>
          )}
          {block.url && <ArrowUpRight className="absolute right-5 top-5 h-4 w-4 text-brand-dark/50 dark:text-white/60" />}
        </div>
      );
      return block.url ? (
        <SmartLink href={block.url} className="block h-full">
          {body}
        </SmartLink>
      ) : (
        body
      );
    }

    case 'marquee':
      return <MarqueeBlock block={block} imageUrls={imageUrls} />;

    case 'team':
      return <TeamBlock title={block.title} />;
    case 'progress':
      return <ProgressBlock title={block.title} />;
    case 'news':
      return <NewsBlock title={block.title} />;
    case 'video':
      return <VideoBlock title={block.title} url={block.url} />;

    default:
      return null;
  }
}


const HERO_SIZE = { md: 'min-h-[280px]', lg: 'min-h-[400px]', xl: 'min-h-[520px]' } as const;

/**
 * Hero (Fase 2): preset de fondo + alineación + tamaño + eyebrow + 2 CTAs.
 * `style` ausente o 'classic' = exactamente lo de siempre (imagen + overlay),
 * así las páginas guardadas antes no cambian. Si hay imagen y un preset, la
 * imagen va detrás con mix-blend luminosity al 35% y el preset sigue viéndose.
 */
function HeroBlock({
  block,
  imageUrls,
  vars,
}: {
  block: Extract<LandingBlock, { type: 'hero' }>;
  imageUrls: Record<string, string>;
  vars: LandingVars;
}) {
  const { ref, onMouseMove, onMouseLeave } = useHeroPointer<HTMLDivElement>();
  const style = block.style ?? 'classic';
  const align = block.align ?? 'center';
  const size = block.size ?? 'md';
  const url = block.imageKey ? imageUrls[block.imageKey] : undefined;
  const dark = style === 'classic' || style === 'spotlight';
  const title = applyLandingVars(block.title, vars);
  const subtitle = applyLandingVars(block.subtitle, vars);
  const eyebrow = applyLandingVars(block.eyebrow, vars);

  return (
    <div
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className={cn(
        'relative overflow-hidden rounded-[20px] flex border border-border/60',
        HERO_SIZE[size],
        ALIGN_ITEMS[align],
        ALIGN_JUSTIFY[align]
      )}
    >
      {/* Fondo */}
      {style === 'classic' ? (
        <>
          {url ? (
            <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-linear-to-br from-brand-purple-pill via-brand-purple to-brand-green" />
          )}
          <div className="absolute inset-0 bg-black/45" />
        </>
      ) : (
        <>
          {style === 'aurora' && <AuroraBackground mood={block.mood} />}
          {style === 'spotlight' && <SpotlightBackground mood={block.mood} />}
          {style === 'grid' && <GridBackground />}
          {url && (
            <img
              src={url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-35 pointer-events-none"
              style={{ mixBlendMode: 'luminosity' }}
            />
          )}
        </>
      )}

      {/* Contenido */}
      <div
        className={cn(
          'relative z-10 flex flex-col gap-4 px-6 py-12 md:px-14 md:py-14 max-w-3xl',
          ALIGN_TEXT[align],
          ALIGN_ITEMS[align]
        )}
      >
        {eyebrow && (
          <span className={cn(EYEBROW, dark && style === 'classic' && 'text-white/80')}>{eyebrow}</span>
        )}
        <h1
          className={cn(
            DISPLAY_TITLE,
            'text-4xl md:text-6xl leading-[1.05] text-balance',
            style === 'spotlight' ? 'pl-shimmer' : dark ? 'text-white drop-shadow-xs' : 'text-brand-dark dark:text-white'
          )}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            className={cn(
              'text-base md:text-lg max-w-xl text-pretty',
              dark ? 'text-white/85' : 'text-brand-dark/70 dark:text-white/75'
            )}
          >
            {subtitle}
          </p>
        )}
        {((block.ctaLabel && block.ctaUrl) || (block.ctaSecondaryLabel && block.ctaSecondaryUrl)) && (
          <div className={cn('mt-3 flex flex-wrap gap-3', ALIGN_JUSTIFY[align])}>
            {block.ctaLabel && block.ctaUrl && <ButtonColorful href={block.ctaUrl} label={block.ctaLabel} />}
            {block.ctaSecondaryLabel && block.ctaSecondaryUrl && (
              <ButtonColorful variant="secondary" href={block.ctaSecondaryUrl} label={block.ctaSecondaryLabel} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/** Cards (Fase 1 hover). Los ítems solo-imagen (fotos) abren el lightbox del bloque. */
function CardsBlock({ block, imageUrls }: { block: Extract<LandingBlock, { type: 'cards' }>; imageUrls: Record<string, string> }) {
  const photos = block.items.filter((i) => i.imageKey && !i.title && !i.text && !i.url && imageUrls[i.imageKey!]);
  const gallery = photos.map((i) => imageUrls[i.imageKey!]);
  const lightbox = useLightbox(gallery);
  const openFor = (item: (typeof block.items)[number]) => {
    const idx = photos.findIndex((p) => p.id === item.id);
    if (idx >= 0) lightbox.open(idx);
  };
      const cols = block.columns ?? 3;
      return (
        <div>
          {block.title && (
            <h2 className={cn(DISPLAY_TITLE, 'text-3xl md:text-4xl mb-5', ALIGN_TEXT[block.align ?? 'left'])}>{block.title}</h2>
          )}
          <div
            className={cn(
              'grid gap-4',
              ALIGN_TEXT[block.align ?? 'left'],
              cols === 2 && 'grid-cols-1 md:grid-cols-2',
              cols === 3 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
              cols === 4 && 'grid-cols-2 md:grid-cols-4'
            )}
          >
            {block.items.map((item) => {
              const imgUrl = item.imageKey ? imageUrls[item.imageKey] : undefined;
              const body = (
                <div className={cn('group h-full overflow-hidden', CARD_HOVER)}>
                  {item.imageKey &&
                    (imgUrl ? (
                      // Tarjetas con texto = ícono/ilustración (contain, sin recortar);
                      // tarjetas solo-imagen = foto (cover, tile).
                      item.title || item.text ? (
                        <div className="flex items-center justify-center h-36 p-4 bg-muted/20">
                          <img
                            src={imgUrl}
                            alt={item.title ?? ''}
                            className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                          />
                        </div>
                      ) : (
                        <img src={imgUrl} alt="" className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                      )
                    ) : (
                      <div className="aspect-video">
                        <MissingImage />
                      </div>
                    ))}
                  {(item.title || item.text) && (
                    <div className="p-4 space-y-2">
                      {item.title && (
                        <h3 className={cn(DISPLAY_TITLE, 'text-xl leading-snug')}>{item.title}</h3>
                      )}
                      {item.text && (
                        <div className="text-sm text-muted-foreground [&_p]:text-sm [&_p]:text-muted-foreground [&_li]:text-sm">
                          <LandingMarkdown content={item.text} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
              if (item.url) {
                return (
                  <SmartLink key={item.id} href={item.url} className="block h-full">
                    {body}
                  </SmartLink>
                );
              }
              const isPhoto = photos.some((p) => p.id === item.id);
              return isPhoto ? (
                <button key={item.id} type="button" className="block h-full w-full text-left cursor-zoom-in" onClick={() => openFor(item)}>
                  {body}
                </button>
              ) : (
                <div key={item.id} className="h-full">
                  {body}
                </div>
              );
            })}
          </div>
      {lightbox.node}
    </div>
  );
}

function MarqueeBlock({ block, imageUrls }: { block: Extract<LandingBlock, { type: 'marquee' }>; imageUrls: Record<string, string> }) {
  const imgs = block.imageKeys.map((k) => imageUrls[k]).filter(Boolean);
  const lightbox = useLightbox(imgs);
  if (imgs.length === 0) return <MissingImage label={block.title} />;
  // Lista duplicada para el loop CSS (pl-marquee: 30s lineal, pausa en hover,
  // estático con reduced-motion y en mobile). Click → lightbox con anterior/siguiente.
  const loop = [...imgs, ...imgs];
  return (
    <div className="space-y-4">
      {block.title && <span className={EYEBROW}>{block.title}</span>}
      <div className="overflow-hidden mask-[linear-gradient(to_right,transparent,#000_8%,#000_92%,transparent)]">
        <div className="flex w-max gap-4 pl-marquee">
          {loop.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              className="shrink-0 cursor-zoom-in rounded-2xl transition-transform duration-300 hover:scale-[1.02] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-green"
              onClick={() => lightbox.open(i % imgs.length)}
            >
              <img src={src} alt="" loading="lazy" className="h-40 md:h-52 w-auto rounded-2xl object-cover border border-border/60" />
            </button>
          ))}
        </div>
      </div>
      {lightbox.node}
    </div>
  );
}

/** Imagen suelta: click para ampliar (sin link propio). */
function ZoomableImage({ url, children }: { url: string; children: React.ReactNode }) {
  const lightbox = useLightbox([url]);
  return (
    <>
      <button type="button" className="cursor-zoom-in rounded-xl focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-green" onClick={() => lightbox.open(0)}>
        {children}
      </button>
      {lightbox.node}
    </>
  );
}
